import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import React, { useRef, useState, ChangeEvent } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import SignaturePad from "react-signature-pad-wrapper";
import { Workpack, EmailOption } from "../helper";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";


interface SignatureProps {
  show: boolean;
  workpack: Workpack | null;
  onHide: () => void;
  allEmail: EmailOption[] | null;
  projectId: String | null;
  taskId: String | null | undefined;
  siteId: String | null;
  workpackId: String | null;
  fetchData: () => void;
}

// Mapping of signature types to human-readable names
const signatureTypeLabels: Record<string, string> = {
  onshore: "Onshore",
  offshore: "Offshore",
  onshore1: "Onshore1",
  material: "Material",
  construction: "Construction",
};

const SignatureModal: React.FC<SignatureProps> = ({
  show,
  onHide,
  workpack,
  allEmail,
  taskId,
  projectId,
  siteId,
  workpackId,
  fetchData,
}) => {
  // Refs for each signature pad
  const onshoreSigPadRef = useRef<SignaturePad>(null);
  const onshore1SigPadRef = useRef<SignaturePad>(null);
  const materialSigPadRef = useRef<SignaturePad>(null);
  const offshoreSigPadRef = useRef<SignaturePad>(null);
  const leaderSigPadRef = useRef<SignaturePad>(null);
  const [email, setEmail] = useState<string[]>([]);
  const [customEmail, setCustomEmail] = useState<string>("");
  const [showCustomInput, setShowCustomInput] = useState<boolean>(false);
  const [showEmailSection, setShowEmailSection] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const getEmailOptionsByType = (type: string) => {
    if (!allEmail) return [];
    
    const operationMap: Record<string, string[]> = {
      onshore: ['Onshore Coordinator'],
      offshore: ['Offshore Supervisor'],
      onshore1: ['Onshore Coordinator'],
      material: ['Material Controller'],
      construction: ['Lead']
    };

    // Determine which type of emails to show based on the current signature state
    let currentType = type;
    if (workpack?.onshore === false ) {
      currentType = 'offshore';
    } else if (workpack?.onshore === true && workpack?.offshore === false) {
      currentType = 'onshore1';
    } else if (workpack?.offshore === true && workpack?.onshore1 === false) {
      currentType = 'material';
    } else if (workpack?.onshore1 === true && workpack?.material === false) {
      currentType = 'construction';
    }

    const roleBasedOptions = allEmail
      .filter(option => operationMap[currentType]?.includes(option.Operation))
      .map(option => ({
        label: `${option.email} (${option.Operation})`,
        value: option.email
      }));

    return [
      ...roleBasedOptions,
      { label: 'Other', value: 'Other' }
    ];
  };

  const handleInputChange = (e: any) => {
    const selectedValues = e.value;
    // Remove duplicates and empty strings
    const uniqueValues = selectedValues
      .filter((value: string) => value.trim() !== "")
      .filter(
        (value: string, index: number, self: string[]) =>
          self.indexOf(value) === index
      );

    // If "Other" is selected, show custom input but keep all selected values
    if (uniqueValues.includes("Other")) {
      setEmail(uniqueValues);
      setShowCustomInput(true);
    } else {
      setEmail(uniqueValues);
      setCustomEmail(""); // Clear custom email when not using "Other"
      setShowCustomInput(false);
    }
  };

  const handleCustomEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value.trim();
    setCustomEmail(emailValue);
  };

  // Helper function to convert data URL to Blob
  const dataURLtoBlob = (dataUrl: string) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Upload a signature to the backend
  const uploadSignature = async (
    signaturePadRef: React.RefObject<SignaturePad>,
    signatureName: string
  ) => {
    setIsUploading(true);
    try {
      if (
        signaturePadRef.current !== null &&
        (signatureName === "construction" || email.length > 0)
      ) {
        // Skip email validation for construction (Lead) signature
        if (signatureName !== "construction") {
          // Check if "Other" is selected but no custom email is provided
          if (email.includes("Other") && !customEmail) {
            alert(
              "Please enter a custom email address when 'Other' is selected."
            );
            setIsUploading(false);
            return;
          }
          
          // Check if no emails are selected (except "Other")
          if (email.length === 0 || (email.length === 1 && email[0] === "Other" && !customEmail)) {
            alert("Please select at least one email address.");
            setIsUploading(false);
            return;
          }
        }

        const signatureDataUrl = signaturePadRef.current.toDataURL();
        const signatureBlob = dataURLtoBlob(signatureDataUrl);

        // Get the human-readable role name
        const signerRole = signatureTypeLabels[signatureName] || signatureName;

        const formData = new FormData();
        formData.append(
          "file",
          signatureBlob,
          `${signatureName}_${taskId}_signature.png`
        );
        formData.append("task_id", taskId as string);
        formData.append("signature_type", signatureName as string);
        formData.append("signer_role", signerRole); // Add the signer's role
        // Prepare email data - include both selected emails and custom email if provided
        let emailData: string[] = [];
        if (signatureName === "construction") {
          emailData = [];
        } else {
          // Filter out "Other" from the email array
          const selectedEmails = email.filter(e => e !== "Other");
          
          // Add custom email if "Other" is selected and custom email is provided
          if (email.includes("Other") && customEmail) {
            emailData = [...selectedEmails, customEmail];
          } else {
            emailData = selectedEmails;
          }
        }
            
        formData.append(
          "email",
          JSON.stringify(emailData)
        );
        formData.append("project_id", projectId as string);
        formData.append("site_id", siteId as string);
        formData.append("wp_id", workpackId as string);

        await axios
          .post(`${API_BASE_URL}/ermeo/upload-file/`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            // ✅ Show success confirmation
          toast.current?.show({
            severity: "success",
            summary: "Signature Uploaded",
            detail: "Your signature has been uploaded successfully.",
            life: 2000,
          });
        
          // ✅ Close the modal after 2 seconds
          setTimeout(() => {
            onHide();
          }, 1500);

            // Count completed signatures
            const completedSignatures = [
              response.data.onshore,
              response.data.offshore,
              response.data.onshore1,
              response.data.material,
              response.data.construction,
            ].filter(Boolean).length;

            // Hide email section after first 4 signatures
            if (completedSignatures >= 4) {
              setShowEmailSection(false);
            }

            if (
              response.data.onshore &&
              response.data.offshore &&
              response.data.onshore1 &&
              response.data.material &&
              response.data.construction
            ) {
              onHide();
            }
          });
      } else {
        if (!signaturePadRef.current) {
          alert("Please sign before uploading.");
        }
      }
    } catch (error) {
      console.error("Error uploading signature:", error);
    } finally {
      fetchData();
      setEmail([]);
      setCustomEmail("");
      setIsUploading(false);
      setShowCustomInput(false);
    }
  };
  // Functions to clear signatures
  const clearSignature = (signaturePadRef: React.RefObject<SignaturePad>) => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  // Footer rendering
  const renderFooter = (
    signaturePadRef: React.RefObject<SignaturePad>,
    signatureName: string
  ) => (
    <div>
      <Button
        label="Clear"
        icon="pi pi-refresh"
        onClick={() => clearSignature(signaturePadRef)}
        className="p-button-secondary mx-2"
      />
      <Button
        label={
          isUploading
            ? "Uploading..."
            : signatureName === "construction"
            ? "Upload"
            : "Upload & Send Mail"
        }
        icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-check"}
        onClick={() => uploadSignature(signaturePadRef, signatureName)}
        className="p-button-primary"
        disabled={isUploading}
      />
    </div>
  );

  return (
    <>

    <Toast ref={toast} />
    <Dialog
      header="Sign Here"
      visible={show}
      style={{ width: "450px" }}
      modal
      onHide={() => {
        setEmail([]);
        setCustomEmail("");
        setShowCustomInput(false);
        setShowEmailSection(true);
        onHide();
      }}
    >
      <div>
        {/* Email Section */}
        {showEmailSection &&
          !(
            workpack?.construction === false && workpack?.material === true
          ) && 
          // Don't show email section for construction (Lead) signature
          !(workpack?.material === true && workpack?.onshore1 === true) && (
            <div>
              <label>Email: </label>
              <div className="email-search-container">
                <MultiSelect
                  value={email}
                  options={getEmailOptionsByType(
                    workpack?.onshore === false ? 'onshore' :
                    workpack?.offshore === false ? 'offshore' :
                    workpack?.material === false ? 'material' :
                    'construction'
                  )}
                  onChange={handleInputChange}
                  placeholder="Select emails"
                  className="w-full"
                  maxSelectedLabels={2}
                  selectedItemsLabel="{0} emails selected"
                  showSelectAll={false}
                  optionLabel="label"
                  optionValue="value"
                />
              </div>

              {showCustomInput && (
                <div className="custom-email-container">
                  <label>Enter Custom Email: </label>
                  <input
                    type="email"
                    value={customEmail}
                    className="input"
                    onChange={handleCustomEmailChange}
                    placeholder="your@email.com"
                  />
                </div>
              )}
            </div>
          )}
        {/* Signed Messaged Section */}
        <div>
          {workpack?.onshore === true && (
            <>
              <div className="signature">Onshore has Signed</div>
            </>
          )}
          {workpack?.offshore === true && (
            <>
              <div className="signature">OffShore has Signed</div>
            </>
          )}
          {workpack?.onshore1 === true && (
            <>
              <div className="signature">Onshore has Signed</div>
            </>
          )}
          {workpack?.material === true && (
            <>
              <div className="signature">Material Controller has Signed</div>
            </>
          )}
        </div>
        {workpack?.onshore === false && (
          <>
            <h4>Onshore Signature</h4>
            <SignaturePad
              ref={onshoreSigPadRef}
              options={{
                penColor: "black",
                backgroundColor: "rgba(255, 255, 255, 0)",
              }}
            />
            {renderFooter(onshoreSigPadRef, "onshore")}
          </>
        )}

        {workpack?.offshore === false && workpack?.onshore === true && (
          <>
            <h4>Offshore Signature</h4>
            <SignaturePad
              ref={offshoreSigPadRef}
              options={{
                penColor: "black",
                backgroundColor: "rgba(255, 255, 255, 0)",
              }}
            />
            {renderFooter(offshoreSigPadRef, "offshore")}
          </>
        )}
        {workpack?.onshore1 === false && workpack?.offshore === true && (
          <>
            <h4>Onshore Signature</h4>
            <SignaturePad
              ref={onshore1SigPadRef}
              options={{
                penColor: "black",
                backgroundColor: "rgba(255, 255, 255, 0)",
              }}
            />
            {renderFooter(onshore1SigPadRef, "onshore1")}
          </>
        )}

        {workpack?.material === false && workpack?.onshore1 === true && (
          <>
            <h4>Material Controller Signature</h4>
            <SignaturePad
              ref={materialSigPadRef}
              options={{
                penColor: "black",
                backgroundColor: "rgba(255, 255, 255, 0)",
              }}
            />
            {renderFooter(materialSigPadRef, "material")}
          </>
        )}
        {workpack?.construction === false && workpack?.material === true && (
          <>
            <h4>Construction Leader Signature</h4>
            <SignaturePad
              ref={leaderSigPadRef}
              options={{
                penColor: "black",
                backgroundColor: "rgba(255, 255, 255, 0)",
              }}
            />
            {renderFooter(leaderSigPadRef, "construction")}
          </>
        )}
      </div>
    </Dialog>
    </>
  );
};

export default SignatureModal;